import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { useMediaQuery } from "@/lib/use-media-query";
import { useSchoolScope } from "@/lib/use-school-scope";
import * as api from "@/lib/api";
import type {
  MeDashboardResponse,
  DashboardActionItem,
  DashboardUpcomingEvent,
  CoachActionItem,
  CoachActionType,
  CoachNextFixture,
  CoachReadinessChip,
  StaffDashboardBlock,
  CoachDashboardBlock,
  FamilyDashboardBlock,
} from "@dugads/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MetricCard } from "@/components/metric-card";
import { PendingSignupsCard } from "@/components/pending-signups-card";
import { AvailabilityRequestsCard } from "@/components/availability-requests-card";
import {
  FileWarning,
  MailWarning,
  ShieldAlert,
  Users,
  Receipt,
  CalendarDays,
  HandHelping,
  ClipboardCheck,
  ListChecks,
  UserPlus,
  UserCheck,
  MessageSquare,
  CalendarCheck,
  CalendarPlus,
  Bell,
  Clock,
  MapPin,
  Check,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";

const formatEventDateTime = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

// Icon per coach action type. The map covers reserved types too, so a row that
// a later iteration (138/140/114) lights up renders without a frontend change.
const COACH_ACTION_ICON: Record<CoachActionType, React.ComponentType<{ className?: string }>> = {
  lineups_to_set: ListChecks,
  results_to_record: ClipboardCheck,
  roster_gaps: UserPlus,
  volunteer_gaps: HandHelping,
  availability_pending: CalendarCheck,
  messages_awaiting_reply: MessageSquare,
  fixture_invites: CalendarPlus,
  pending_signups: UserCheck,
};

export default function DashboardPage() {
  const { user } = useAuth();
  const {
    schools,
    selectedSchoolId,
    setSelectedSchoolId,
    loading: schoolsLoading,
    canPickSchool,
  } = useSchoolScope();

  const [data, setData] = useState<MeDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 186: below md we render a purpose-built mobile dashboard (one tree, not the
  // desktop grid reflowed) -- same data, same _actions, same hooks.
  const isMdUp = useMediaQuery("(min-width: 768px)");

  // Sponsor-only users get redirected to their dedicated dashboard.
  const isSponsorOnly =
    user &&
    user.roles.some(
      (r) => (r.role_type === "sponsor_admin" || r.role_type === "sponsor_user") && r.scope_type === "sponsor",
    ) &&
    !user.roles.some(
      (r) =>
        r.role_type === "super_admin" ||
        r.role_type === "org_admin" ||
        r.role_type === "school_admin" ||
        r.role_type === "coach" ||
        r.role_type === "super_parent" ||
        r.role_type === "parent",
    );

  // Fetch once the school scope has settled. For multi-school admins we pass the
  // selected school; everyone else lets the endpoint default to their own scope.
  useEffect(() => {
    if (isSponsorOnly || schoolsLoading) return;
    setLoading(true);
    api
      .getMeDashboard(canPickSchool ? selectedSchoolId || undefined : undefined)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load dashboard"))
      .finally(() => setLoading(false));
  }, [isSponsorOnly, schoolsLoading, canPickSchool, selectedSchoolId]);

  // 199 -- POPIA consent still outstanding for any linked child surfaces as a
  // needs-attention card (one batch call; the approve dialog lives on
  // /my-children, the dashboard only signposts it).
  const hasFamilyChildren = Boolean(data?.family && data.family.children.length > 0);
  const [consentPending, setConsentPending] = useState(0);
  useEffect(() => {
    if (!hasFamilyChildren) {
      setConsentPending(0);
      return;
    }
    let cancelled = false;
    api
      .getChildrenConsentStatus()
      .then((res) => {
        if (cancelled) return;
        setConsentPending(res.children.filter((c) => c.is_minor && c.consent_required && !c.consented).length);
      })
      .catch(() => {
        if (!cancelled) setConsentPending(0);
      });
    return () => {
      cancelled = true;
    };
  }, [hasFamilyChildren]);

  if (isSponsorOnly) return <Navigate to="/my-sponsor/dashboard" replace />;

  const firstName = user?.user.first_name?.trim();
  const greeting = firstName ? `Welcome back, ${firstName}` : "Welcome back";

  if (loading && !data) {
    return (
      <p className="text-muted-foreground" data-page="dashboard" data-state="loading">
        Loading dashboard...
      </p>
    );
  }
  if (error) {
    return (
      <div
        className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive"
        data-page="dashboard"
        data-state="error"
        data-error
      >
        {error}
      </div>
    );
  }

  const staff = data?.staff;
  const coach = data?.coach;
  const family = data?.family;

  const hasAnyBlock = Boolean(staff || coach || family);

  // Staff "needs attention" -- only items with a non-zero count earn a card.
  const staffAttention: Array<{
    key: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    item: DashboardActionItem;
  }> = staff
    ? [
        { key: "draft_events", label: "Draft events to publish", icon: FileWarning, item: staff.needs_attention.draft_events },
        { key: "unclaimed_parent_invites", label: "Unclaimed parent invites", icon: MailWarning, item: staff.needs_attention.unclaimed_parent_invites },
        { key: "players_missing_indemnity", label: "Players missing indemnity", icon: ShieldAlert, item: staff.needs_attention.players_missing_indemnity },
        { key: "open_volunteer_slots", label: "Unfilled volunteer slots", icon: HandHelping, item: staff.needs_attention.open_volunteer_slots },
        { key: "placements_expiring_30d", label: "Placements expiring (30d)", icon: Receipt, item: staff.needs_attention.placements_expiring_30d },
      ].filter((a) => a.item.count > 0)
    : [];

  const coachHasContent = Boolean(
    coach &&
      (coach.action_queue.length > 0 ||
        coach.next_fixture !== null ||
        coach.teams.length > 0 ||
        coach.upcoming_events.length > 0),
  );

  // Mobile: render the dedicated mobile dashboard instead of the desktop grid.
  const isOrgAdmin = Boolean(user?.roles.some((r) => r.role_type === "org_admin"));
  if (!isMdUp) {
    return (
      <MobileDashboard
        firstName={firstName}
        staff={staff}
        coach={coach}
        family={family}
        staffAttention={staffAttention}
        schools={schools}
        selectedSchoolId={selectedSchoolId}
        setSelectedSchoolId={setSelectedSchoolId}
        canPickSchool={canPickSchool}
        isOrgAdmin={isOrgAdmin}
        consentPending={consentPending}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-page="dashboard" data-state="ready">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-region="header">
        <div>
          <h1 className="text-2xl font-semibold">{greeting}</h1>
          <p className="text-muted-foreground">Here's what needs you and what's coming up.</p>
        </div>
        {staff && canPickSchool && (
          <div className="flex items-center gap-2">
            <label htmlFor="dash-school" className="text-sm text-muted-foreground">
              School
            </label>
            <select
              id="dash-school"
              data-field="school"
              value={selectedSchoolId}
              onChange={(e) => setSelectedSchoolId(e.target.value)}
              className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">-- Select school --</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Multi-school admin who hasn't picked a school yet */}
      {staff && staff.scope === null && (
        <Card data-region="staff-pick-school">
          <CardContent className="py-8 text-center text-muted-foreground">
            Select a school above to see what needs attention.
          </CardContent>
        </Card>
      )}

      {/* Needs Attention (staff) -- hidden entirely when nothing is outstanding */}
      {staff && staffAttention.length > 0 && (
        <section className="flex flex-col gap-3" data-region="staff-attention">
          <h2 className="text-lg font-medium">Needs attention</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {staffAttention.map((a) => (
              <Link key={a.key} to={a.item.link} className="block" data-attention={a.key}>
                <MetricCard icon={a.icon} label={a.label} value={a.item.count} highlight />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Upcoming Events (staff) */}
      {staff && staff.scope !== null && staff.upcoming_events.length > 0 && (
        <UpcomingEventsCard
          region="staff-schedule"
          title="Upcoming events"
          events={staff.upcoming_events}
        />
      )}

      {/* Coach command center -- action queue + next-fixture readiness */}
      {coach && coachHasContent && (
        <section className="flex flex-col gap-4" data-region="coach">
          <h2 className="text-lg font-medium">Coach command center</h2>

          {/* Action queue -- every tile an open loop with a verb. Hidden when empty. */}
          {coach.action_queue.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-region="coach-action-queue">
              {coach.action_queue.map((a) => (
                <CoachActionTile key={a.type} action={a} />
              ))}
            </div>
          )}

          {/* 190 -- pending parent self-serve signups awaiting approval (self-hides when empty) */}
          <PendingSignupsCard />

          {/* Next-fixture readiness card */}
          {coach.next_fixture && <NextFixtureCard fixture={coach.next_fixture} />}

          {/* Teams at a glance */}
          {coach.teams.length > 0 && (
            <div className="flex flex-wrap gap-2" data-region="coach-teams">
              {coach.teams.map((t) => {
                const empty = t.player_count === 0;
                return (
                  <Link
                    key={t.id}
                    to={`/teams/${t.id}`}
                    className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm transition-colors hover:bg-muted"
                    data-team-id={t.id}
                  >
                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    {t.name}
                    <Badge
                      variant={empty ? "outline" : "secondary"}
                      className={empty ? "border-amber-400 text-amber-700" : undefined}
                    >
                      {empty ? "no players" : t.player_count}
                    </Badge>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Full near-term schedule */}
          {coach.upcoming_events.length > 0 ? (
            <UpcomingEventsCard
              region="coach-schedule"
              title="Your teams' next events"
              events={coach.upcoming_events}
              extraHeader="Match-up"
              extraCell={(e) => {
                const mine = "my_teams" in e ? (e as { my_teams: string[] }).my_teams.join("/") : "";
                const opp = "opponents" in e ? (e as { opponents: string[] }).opponents : [];
                return opp.length > 0 ? `${mine} vs ${opp.join(", ")}` : mine;
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground" data-region="coach-schedule-empty">
              No matches scheduled for your teams in the next 14 days.
            </p>
          )}
        </section>
      )}

      {/* Family -- volunteer asks + children's fixtures. 199: also consent
          signpost, pending availability replies, and the children themselves. */}
      {family && (family.children.length > 0 || family.upcoming_events.length > 0) && (
        <section className="flex flex-col gap-4" data-region="family">
          <h2 className="text-lg font-medium">Your family</h2>
          {consentPending > 0 && (
            <Link to="/my-children" className="block" data-attention="consent_pending">
              <MetricCard icon={ShieldAlert} label="Consent approvals needed" value={consentPending} highlight />
            </Link>
          )}
          {family.needs_attention.pending_volunteer_requests.count > 0 && (
            <Link to={family.needs_attention.pending_volunteer_requests.link} className="block" data-attention="pending_volunteer_requests">
              <MetricCard
                icon={HandHelping}
                label="Volunteer slots you could fill"
                value={family.needs_attention.pending_volunteer_requests.count}
                highlight
              />
            </Link>
          )}
          <AvailabilityRequestsCard />
          {family.children.length > 0 && (
            <div className="flex flex-wrap gap-2" data-region="family-children">
              {family.children.map((c) => (
                <Link
                  key={c.id}
                  to="/my-children"
                  data-child-id={c.id}
                  className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm transition-colors hover:bg-muted"
                >
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  {c.name}
                  {c.school_name && <span className="text-xs text-muted-foreground">{c.school_name}</span>}
                </Link>
              ))}
            </div>
          )}
          {family.upcoming_events.length > 0 && (
            <UpcomingEventsCard
              region="family-schedule"
              title="Your children's next events"
              events={family.upcoming_events}
              extraHeader="Playing"
              extraCell={(e) => ("child_names" in e ? (e as { child_names: string[] }).child_names.join(", ") : "")}
            />
          )}
        </section>
      )}

      {/* Nothing to show (e.g. a stub/player-only user landing here) */}
      {!hasAnyBlock && (
        <Card data-region="empty">
          <CardContent className="py-8 text-center text-muted-foreground">
            Nothing needs your attention right now.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// A single open-loop tile in the coach action queue. `high` severity gets the
// amber "needs you now" treatment; the whole tile deep-links to its resolution.
function CoachActionTile({ action }: { action: CoachActionItem }) {
  const Icon = COACH_ACTION_ICON[action.type] ?? Bell;
  const high = action.severity === "high";
  return (
    <Link
      to={action.link}
      className={`group block rounded-lg border p-4 transition-colors ${
        high ? "border-amber-300 bg-amber-50 hover:bg-amber-100" : "border-border hover:bg-muted"
      }`}
      data-action-type={action.type}
      data-severity={action.severity}
    >
      <div className="flex items-start gap-3">
        <div className={`rounded-md p-2 ${high ? "bg-amber-500/15" : "bg-primary/10"}`}>
          <Icon className={`h-5 w-5 ${high ? "text-amber-600" : "text-primary"}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{action.label}</p>
          {action.context && (
            <p className="truncate text-xs text-muted-foreground">{action.context}</p>
          )}
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
    </Link>
  );
}

// Readiness chip on the next-fixture card. ok = green, warn = amber, info = neutral.
function ReadinessChip({ chip }: { chip: CoachReadinessChip }) {
  const tone =
    chip.state === "warn"
      ? "border-amber-300 bg-amber-50 text-amber-700"
      : chip.state === "ok"
        ? "border-emerald-300 bg-emerald-50 text-emerald-700"
        : "border-border bg-muted text-muted-foreground";
  const ChipIcon = chip.key === "venue" ? MapPin : chip.state === "ok" ? Check : AlertTriangle;
  const inner = (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${tone}`}
      data-chip={chip.key}
      data-chip-state={chip.state}
    >
      <ChipIcon className="h-3.5 w-3.5" />
      {chip.label}
    </span>
  );
  return chip.link ? (
    <Link to={chip.link} className="transition-opacity hover:opacity-80">
      {inner}
    </Link>
  ) : (
    inner
  );
}

// The coach's single most imminent fixture, as a mini command center: who/when/
// where plus readiness chips (lineup, volunteers, venue) with one-tap fixes.
function NextFixtureCard({ fixture }: { fixture: CoachNextFixture }) {
  const e = fixture.event;
  const mine = e.my_teams.join("/");
  const matchup = e.opponents.length > 0 ? `${mine} vs ${e.opponents.join(", ")}` : mine;
  return (
    <Card data-region="coach-next-fixture">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarDays className="h-4 w-4" />
          Next fixture
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div>
          <p className="font-medium">{e.name}</p>
          {matchup && <p className="text-sm text-muted-foreground">{matchup}</p>}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            {formatEventDateTime(fixture.kickoff_at)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />
            {e.venue ?? "Venue TBC"}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {fixture.chips.map((c) => (
            <ReadinessChip key={c.key} chip={c} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function UpcomingEventsCard({
  region,
  title,
  events,
  extraHeader,
  extraCell,
}: {
  region: string;
  title: string;
  events: DashboardUpcomingEvent[];
  extraHeader?: string;
  extraCell?: (e: DashboardUpcomingEvent) => string;
}) {
  return (
    <Card data-region={region}>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarDays className="h-4 w-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>When</TableHead>
                <TableHead>Venue</TableHead>
                {extraHeader && <TableHead>{extraHeader}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((e) => (
                <TableRow key={e.id} data-event-id={e.id}>
                  <TableCell className="font-medium">
                    {e.name}
                    <span className="block text-xs text-muted-foreground">{e.school.name}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{formatEventDateTime(e.starts_at)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{e.venue ?? "--"}</TableCell>
                  {extraHeader && (
                    <TableCell className="text-sm text-muted-foreground">{extraCell?.(e) || "--"}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// 186: Mobile dashboard (< md). A dedicated mobile presentation -- NOT the
// desktop grid reflowed -- built to match the design prototype, reusing the
// page's data, _actions links and helpers. Rendered as the only tree below md.
// ============================================================================

// event_type -> dot colour + type label + pill text colour (match_day/practice/
// tryout per the design tokens: blue / green / orange).
const EVENT_TYPE_META: Record<string, { dot: string; text: string; label: string }> = {
  match_day: { dot: "bg-blue-600", text: "text-blue-700", label: "Match Day" },
  practice: { dot: "bg-green-600", text: "text-green-700", label: "Practice" },
  tryout: { dot: "bg-orange-600", text: "text-orange-700", label: "Tryout" },
};
function eventTypeMeta(t: string) {
  return EVENT_TYPE_META[t] ?? { dot: "bg-muted-foreground", text: "text-muted-foreground", label: t };
}

// Tinted icon-chip tones for the needs-attention cards.
const ATTENTION_TONE: Record<string, string> = {
  amber: "bg-amber-100 text-amber-700",
  red: "bg-red-100 text-red-600",
  blue: "bg-blue-100 text-blue-600",
  indigo: "bg-indigo-100 text-indigo-600",
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <h2 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{children}</h2>;
}

// A 138px needs-attention card: tinted icon chip, big count, label. Scrolls
// horizontally in a row with its siblings.
function MobileAttentionCard({
  icon: Icon,
  count,
  label,
  to,
  tone,
  dataKey,
}: {
  icon: React.ComponentType<{ className?: string }>;
  count: number;
  label: string;
  to: string;
  tone: string;
  dataKey: string;
}) {
  return (
    <Link
      to={to}
      data-attention={dataKey}
      className="flex w-[138px] shrink-0 flex-col rounded-2xl border border-border bg-card p-3 text-left shadow-sm transition-transform active:scale-[.97]"
    >
      <div className={`mb-2 flex h-9 w-9 items-center justify-center rounded-[10px] ${ATTENTION_TONE[tone] ?? ATTENTION_TONE.blue}`}>
        <Icon className="h-[18px] w-[18px]" />
      </div>
      <div className="text-[22px] font-extrabold leading-none">{count}</div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </Link>
  );
}

// A near-term event as a card row: left date block, name + time/venue, type dot.
function MobileEventRow({ event }: { event: DashboardUpcomingEvent }) {
  const d = new Date(event.starts_at);
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const meta = eventTypeMeta(event.event_type);
  return (
    <Link
      to={`/events/${event.id}`}
      data-event-id={event.id}
      className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm transition-transform active:scale-[.99]"
    >
      <div className="w-11 shrink-0 text-center">
        <div className="text-lg font-extrabold leading-none">{d.getDate()}</div>
        <div className="text-[11px] font-semibold uppercase text-muted-foreground">
          {d.toLocaleDateString(undefined, { month: "short" })}
        </div>
      </div>
      <div className="h-9 w-px shrink-0 bg-border" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{event.name}</p>
        <p className="truncate text-xs text-muted-foreground">
          {time}
          {event.venue ? ` · ${event.venue}` : ""}
        </p>
      </div>
      <span className={`h-2 w-2 shrink-0 rounded-full ${meta.dot}`} />
    </Link>
  );
}

// The coach's next-fixture readiness card, stacked for mobile (chips wrap).
// 204 -- the card is a clickable div (navigate), NOT a <Link>: the readiness
// chips inside render their own <Link>s and nested anchors are invalid HTML
// (React hydration error; unpredictable tap targets).
function MobileNextFixture({ fixture }: { fixture: CoachNextFixture }) {
  const navigate = useNavigate();
  const e = fixture.event;
  const mine = e.my_teams.join("/");
  const matchup = e.opponents.length > 0 ? `${mine} vs ${e.opponents.join(", ")}` : mine;
  const meta = eventTypeMeta(e.event_type);
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/events/${e.id}`)}
      onKeyDown={(ev) => {
        if (ev.key === "Enter" || ev.key === " ") navigate(`/events/${e.id}`);
      }}
      data-region="coach-next-fixture"
      data-event-id={e.id}
      className="block cursor-pointer rounded-2xl border border-border bg-card p-4 shadow-sm transition-transform active:scale-[.99]"
    >
      <div className="mb-2.5 flex items-center gap-2">
        <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
        <span className={`text-xs font-semibold uppercase tracking-wide ${meta.text}`}>{meta.label}</span>
      </div>
      <p className="text-lg font-bold">{e.name}</p>
      {matchup && <p className="mb-3 text-sm text-muted-foreground">{matchup}</p>}
      <div className="mb-3.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-foreground/80">
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          {formatEventDateTime(fixture.kickoff_at)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
          {e.venue ?? "Venue TBC"}
        </span>
      </div>
      {/* Chip links must not also fire the card's navigate. */}
      <div className="flex flex-wrap gap-2" onClick={(ev) => ev.stopPropagation()}>
        {fixture.chips.map((c) => (
          <ReadinessChip key={c.key} chip={c} />
        ))}
      </div>
    </div>
  );
}

interface MobileDashboardProps {
  firstName?: string;
  staff?: StaffDashboardBlock;
  coach?: CoachDashboardBlock;
  family?: FamilyDashboardBlock;
  staffAttention: Array<{
    key: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    item: DashboardActionItem;
  }>;
  schools: Array<{ id: string; name: string }>;
  selectedSchoolId: string;
  setSelectedSchoolId: (id: string) => void;
  canPickSchool: boolean;
  isOrgAdmin: boolean;
  consentPending: number;
}

function MobileDashboard({
  firstName,
  staff,
  coach,
  family,
  staffAttention,
  schools,
  selectedSchoolId,
  setSelectedSchoolId,
  canPickSchool,
  isOrgAdmin,
  consentPending,
}: MobileDashboardProps) {
  const greeting = firstName ? `Welcome back, ${firstName}` : "Welcome back";
  const dateLine = new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

  // Needs-attention cards: staff signals + coach open loops + family volunteer ask.
  const attention: Array<{ key: string; icon: React.ComponentType<{ className?: string }>; count: number; label: string; to: string; tone: string }> = [];
  staffAttention.forEach((a) =>
    attention.push({
      key: `staff:${a.key}`,
      icon: a.icon,
      count: a.item.count,
      label: a.label,
      to: a.item.link,
      tone: a.key.includes("indemnity") ? "red" : a.key.includes("volunteer") ? "amber" : "blue",
    }),
  );
  coach?.action_queue.forEach((a) =>
    attention.push({
      key: `coach:${a.type}`,
      icon: COACH_ACTION_ICON[a.type] ?? Bell,
      count: a.count,
      label: a.label,
      to: a.link,
      tone: a.severity === "high" ? "red" : "indigo",
    }),
  );
  if (family && family.needs_attention.pending_volunteer_requests.count > 0) {
    attention.push({
      key: "family:volunteer",
      icon: HandHelping,
      count: family.needs_attention.pending_volunteer_requests.count,
      label: "Volunteer slots to fill",
      to: family.needs_attention.pending_volunteer_requests.link,
      tone: "amber",
    });
  }
  // 199 -- outstanding POPIA consent for a linked child is a real open loop.
  if (consentPending > 0) {
    attention.push({
      key: "family:consent",
      icon: ShieldAlert,
      count: consentPending,
      label: "Consent approvals needed",
      to: "/my-children",
      tone: "red",
    });
  }

  // Merge the near-term schedule across whatever blocks the user has.
  const upMap = new Map<string, DashboardUpcomingEvent>();
  staff?.upcoming_events.forEach((e) => upMap.set(e.id, e));
  coach?.upcoming_events.forEach((e) => upMap.set(e.id, e));
  family?.upcoming_events.forEach((e) => upMap.set(e.id, e));
  const upcoming = [...upMap.values()].sort((a, b) => a.starts_at.localeCompare(b.starts_at)).slice(0, 8);

  const teams = coach?.teams ?? [];
  const teamsLabel = family && !coach ? "Your children's teams" : "Your teams";
  const needsSchoolPick = Boolean(staff && staff.scope === null);
  const children = family?.children ?? [];
  const showEmpty =
    !needsSchoolPick && attention.length === 0 && !coach?.next_fixture && upcoming.length === 0 && teams.length === 0 && children.length === 0;

  return (
    <div className="flex flex-col gap-5" data-page="dashboard" data-state="ready" data-view="mobile">
      <div data-region="header">
        <p className="text-[13px] font-medium text-muted-foreground">{dateLine}</p>
        <h1 className="text-2xl font-extrabold tracking-tight">{greeting}</h1>
        <p className="text-[13px] text-muted-foreground">Here's what needs you and what's coming up.</p>
      </div>

      {isOrgAdmin && (
        <Link
          to="/organizations"
          data-region="org-hero"
          className="block rounded-2xl bg-gradient-to-br from-blue-900 to-blue-600 p-4 text-white shadow-sm transition-transform active:scale-[.99]"
        >
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide opacity-80">Organization</span>
            <span className="inline-flex items-center gap-0.5 text-xs font-semibold opacity-90">
              Open <ChevronRight className="h-3.5 w-3.5" />
            </span>
          </div>
          <p className="text-lg font-extrabold">Organization portal</p>
          <p className="text-xs opacity-80">Schools, advertising &amp; field view</p>
        </Link>
      )}

      {staff && canPickSchool && (
        <div className="flex items-center gap-2" data-region="school-picker">
          <label htmlFor="dash-school-m" className="text-sm text-muted-foreground">
            School
          </label>
          <select
            id="dash-school-m"
            data-field="school"
            value={selectedSchoolId}
            onChange={(e) => setSelectedSchoolId(e.target.value)}
            className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">-- Select school --</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {needsSchoolPick && (
        <div
          className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm"
          data-region="staff-pick-school"
        >
          Select a school above to see what needs attention.
        </div>
      )}

      {attention.length > 0 && (
        <section className="flex flex-col gap-2.5" data-region="needs-attention">
          <SectionLabel>Needs attention</SectionLabel>
          <div className="-mx-4 flex gap-2.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {attention.map((a) => (
              <MobileAttentionCard key={a.key} dataKey={a.key} icon={a.icon} count={a.count} label={a.label} to={a.to} tone={a.tone} />
            ))}
          </div>
        </section>
      )}

      {/* 190 -- pending signups approval (self-hides when empty) */}
      {coach && <PendingSignupsCard />}

      {/* 199 -- pending availability replies for the parent's children (138
          card: one-tap In/Maybe/Out, self-hides when nothing is pending) */}
      {family && <AvailabilityRequestsCard />}

      {coach?.next_fixture && (
        <section className="flex flex-col gap-2.5" data-region="coach-next">
          <SectionLabel>Next fixture</SectionLabel>
          <MobileNextFixture fixture={coach.next_fixture} />
        </section>
      )}

      {upcoming.length > 0 && (
        <section className="flex flex-col gap-2.5" data-region="upcoming">
          <div className="flex items-center justify-between">
            <SectionLabel>Upcoming</SectionLabel>
            <Link to="/events" className="text-[13px] font-semibold text-primary">
              See all
            </Link>
          </div>
          <div className="flex flex-col gap-2.5">
            {upcoming.map((e) => (
              <MobileEventRow key={e.id} event={e} />
            ))}
          </div>
        </section>
      )}

      {/* 199 -- the children themselves: name + school card rows, managed on
          My Children (teams, health, consent, enrolment all live there). */}
      {children.length > 0 && (
        <section className="flex flex-col gap-2.5" data-region="family-children">
          <div className="flex items-center justify-between">
            <SectionLabel>Your children</SectionLabel>
            <Link to="/my-children" className="text-[13px] font-semibold text-primary">
              Manage
            </Link>
          </div>
          <div className="flex flex-col gap-2.5">
            {children.map((c) => (
              <Link
                key={c.id}
                to="/my-children"
                data-child-id={c.id}
                className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm transition-transform active:scale-[.99]"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                  {c.name
                    .split(" ")
                    .map((p) => p[0])
                    .filter(Boolean)
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{c.name}</p>
                  {c.school_name && <p className="truncate text-xs text-muted-foreground">{c.school_name}</p>}
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {teams.length > 0 && (
        <section className="flex flex-col gap-2.5" data-region="coach-teams">
          <SectionLabel>{teamsLabel}</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {teams.map((t) => {
              const empty = t.player_count === 0;
              return (
                <Link
                  key={t.id}
                  to={`/teams/${t.id}`}
                  data-team-id={t.id}
                  className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-semibold transition-transform active:scale-[.96]"
                >
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  {t.name}
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${empty ? "bg-amber-100 text-amber-700" : "bg-muted text-muted-foreground"}`}
                  >
                    {empty ? "no players" : t.player_count}
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {showEmpty && (
        <div
          className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground shadow-sm"
          data-region="empty"
        >
          Nothing needs your attention right now.
        </div>
      )}
    </div>
  );
}
